import React from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { Card, CardContent } from '../ui/card';
import { brl, pct, num, Delta } from '../logistics/format';
import { DataTable } from './DataTable';
import { useIsMobile } from './useIsMobile';

const CLS = { A: { color: '#16a34a', bg: 'rgba(22,163,74,0.08)' }, B: { color: '#d97706', bg: 'rgba(217,119,6,0.08)' }, C: { color: '#64748b', bg: 'rgba(100,116,139,0.08)' } };
const Badge = ({ c }) => <span className="inline-block rounded px-1.5 text-xs font-semibold" style={{ color: CLS[c].color, background: CLS[c].bg }}>{c}</span>;

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border bg-popover p-2 text-xs shadow-md max-w-[16rem]">
      <p className="font-medium">{p.produto}</p>
      <p>Classe {p.classe} · {brl(p.receita)} · {pct(p.participacao)} da receita</p>
      <p className="text-muted-foreground">Acumulado: {pct(p.acumulado)}</p>
    </div>
  );
};

const Pareto = ({ rows }) => {
  const mobile = useIsMobile();
  const data = rows.slice(0, mobile ? 20 : 100).map((r, i) => ({ ...r, idx: i + 1 }));
  const areas = ['A', 'B', 'C'].map((c) => {
    const xs = data.filter((d) => d.classe === c).map((d) => d.idx);
    return xs.length ? { c, x1: xs[0], x2: xs[xs.length - 1] } : null;
  }).filter(Boolean);
  return (
    <Card className="min-w-0">
      <CardContent className="p-4">
        <div style={{ width: '100%', height: mobile ? 220 : 320 }} data-testid="pareto-chart">
          <ResponsiveContainer>
            <ComposedChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              {areas.map((a) => <ReferenceArea key={a.c} x1={a.x1} x2={a.x2} yAxisId="r" fill={CLS[a.c].color} fillOpacity={0.08}
                label={{ value: `Classe ${a.c}`, position: 'insideBottomLeft', fontSize: 11, fill: CLS[a.c].color }} />)}
              <XAxis dataKey="idx" tick={mobile ? false : { fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(v)} width={44} />
              <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" width={36} />
              <Tooltip content={<Tip />} />
              <Bar yAxisId="l" dataKey="receita" fill="#5B5BD6" radius={[2, 2, 0, 0]} />
              <Line yAxisId="r" dataKey="acumulado" stroke="#ef4444" dot={false} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Barras: receita por produto (do maior para o menor{rows.length > data.length ? `, ${data.length} primeiros` : ''}). Linha: receita acumulada (%).
        </p>
      </CardContent>
    </Card>
  );
};

const COLS = [
  { key: 'classe', label: 'Classe', render: (r) => <Badge c={r.classe} /> },
  { key: 'produto', label: 'Produto', wrap: true },
  { key: 'receita', label: 'Receita', num: true, fmt: brl },
  { key: 'unidades', label: 'Unidades', num: true, fmt: num },
  { key: 'participacao', label: 'Participação', num: true, fmt: pct },
  { key: 'delta_pct', label: 'Variação', num: true, render: (r) => <Delta value={r.delta_pct} />, csv: (r) => r.delta_pct },
  { key: 'classe_anterior', label: 'Mudança de classe', render: (r) => (!r.classe_anterior ? <span className="text-xs text-muted-foreground">Novo no período</span>
    : r.classe_anterior === r.classe ? <span className="text-xs text-muted-foreground">Sem mudança</span>
      : <span className="text-xs font-medium">{r.classe_anterior} → {r.classe}</span>), csv: (r) => (r.classe_anterior ? `${r.classe_anterior} -> ${r.classe}` : 'novo') },
];

const Variants = ({ r }) => (
  <ul className="text-xs space-y-1" data-testid="abc-variants">
    {r.variantes.map((v) => (
      <li key={v.variante_id || v.sku} className="flex flex-wrap gap-3">
        <span className="font-medium">{v.variacao}</span>{v.sku && <span className="text-muted-foreground">SKU {v.sku}</span>}
        <span className="font-mono">{brl(v.receita)}</span><span className="text-muted-foreground">{num(v.unidades)} un.</span>
      </li>
    ))}
  </ul>
);

export const AbcSection = ({ abc, isPortal }) => (
  <div className="space-y-4">
    {abc.rows.length > 0 && <Pareto rows={abc.rows} />}
    <div className="grid gap-3 sm:grid-cols-3" data-testid="abc-classes">
      {['A', 'B', 'C'].map((c) => (
        <Card key={c} data-testid={`abc-class-${c}`}>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider" style={{ color: CLS[c].color }}>Classe {c}</p>
            <p className="font-mono text-lg font-bold mt-1">{num(abc.classes[c].produtos)} produtos</p>
            <p className="text-xs text-muted-foreground">{pct(abc.classes[c].receita_pct)} da receita</p>
          </CardContent>
        </Card>
      ))}
    </div>
    <DataTable columns={COLS} rows={abc.rows} testId="abc-table" searchKey="produto" csvName={isPortal ? null : 'curva-abc.csv'}
      expand={(r) => <Variants r={r} />} />
  </div>
);
